/**
 * PreLearningQuiz Component  (Round 2: "Pre-Learning Quiz")
 *
 * Closed-book baseline: both players answer the PRE half of the comprehension
 * questions BEFORE any learning. Per question: answer + confidence (0-6). No
 * usefulness/source (those measure "today's session", which hasn't happened).
 * After the questions, a single prediction (0..N, N = pre-half size): how many
 * they think they got right. This is the round's main content — a synchronized barrier.
 *
 * Data:
 * - player.set("quiz_pre_answers", { [qid]: answer })   (string | string[])
 * - player.set("quiz_pre_confidence", { [qid]: 0-6 })
 * - player.set("quiz_pre_prediction", 0-6)              (postdiction of the pre half)
 * - player.set("baseline_state_confidence", {conf_understand, conf_answer})  (2-item
 *     scale, asked after the pre-quiz before any learning — the pre timepoint for the
 *     pre/post state-confidence measure; same scale as postLearning/postChat/postQuiz)
 * - player.set("quiz_pre_partial", true)  while in progress / on auto-save
 * - On completion: quiz_pre_timestamp, quiz_pre_partial = false, then onComplete().
 *
 * Props:
 * - questionIds: string[]  ids of the pre questions (from game.quizSplit.preIds)
 * - onComplete: () => void
 */

import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import React, { useState } from "react";
import { LatexMessage } from "./chat/LatexMessage";
import { ConfidenceScale, isConfidenceComplete, getEmptyConfidenceState } from "./ConfidenceScale";

const containsHtml = (text) => /<[^>]+>/.test(text || "");
const SCALE_0_6 = [0, 1, 2, 3, 4, 5, 6];

export function PreLearningQuiz({ questionIds = [], onComplete }) {
  const player = usePlayer();
  const game = useGame();
  const config = game.get("config");
  const topicName = config?.name || "the topic";

  // Baseline explanation-confidence item, asked after the pre-quiz (before any
  // learning) for BOTH roles. Teacher (marker 0) rates their own ability to
  // explain; Student (marker > 0) rates how well they expect their partner to
  // explain to them. Single-mode learner (marker 0) uses the self wording.
  const marker = player.get("marker");
  const gameMode = game.get("gameMode") || "dyad";
  const isTeacher = gameMode !== "single" && marker === 0;
  const isStudent = gameMode !== "single" && marker > 0;
  // Field + wording depend on role: self-efficacy vs. evaluating the partner.
  const explainField = isStudent ? "baseline_explain_evaluation" : "baseline_explain_ability";

  const allQuestions = player.get("quizQuestions") || config?.quiz?.questions || [];
  const byId = new Map(allQuestions.map((q) => [q.id, q]));
  const questions = questionIds.map((id) => byId.get(id)).filter(Boolean);

  // Resume from any auto-saved partial state
  const [answers, setAnswers] = useState(() => player.get("quiz_pre_answers") || {});
  const [confidence, setConfidence] = useState(() => player.get("quiz_pre_confidence") || {});
  const [prediction, setPrediction] = useState(() => {
    const p = player.get("quiz_pre_prediction");
    return p === undefined ? null : p;
  });
  // Baseline state confidence (2-item scale), asked after the pre-quiz, before
  // any learning — the pre timepoint for the pre/post state-confidence measure.
  const [baselineConfidence, setBaselineConfidence] = useState(() => {
    const saved = player.get("baseline_state_confidence");
    return saved ? { conf_understand: saved.conf_understand, conf_answer: saved.conf_answer } : getEmptyConfidenceState();
  });
  // Baseline point of the explanation-confidence trajectory. Teacher:
  // baseline → post-study → post-teaching → exit (4 points). Student:
  // baseline → post-teaching → exit (3 points). Stored per-role in explainField.
  const [explainAbility, setExplainAbility] = useState(() => {
    const s = player.get(explainField);
    return s && s.value !== undefined ? String(s.value) : null;
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPrediction, setShowPrediction] = useState(false);
  // Extra step shown AFTER the quiz for BOTH roles: the baseline
  // explanation-confidence item, on its own screen.
  const [showExplain, setShowExplain] = useState(false);
  const [showError, setShowError] = useState(false);

  const hasAnswer = (q) => {
    const a = answers[q.id];
    if (q.type === "text") return !!(a && a.trim().length > 0);
    if (q.type === "checkbox") return !!(a && a.length > 0);
    return !!a;
  };
  const hasConfidence = (q) => confidence[q.id] !== null && confidence[q.id] !== undefined;
  const isAnswered = (q) => !!q && hasAnswer(q) && hasConfidence(q);

  const persistAnswers = (next) => {
    player.set("quiz_pre_answers", next);
    player.set("quiz_pre_partial", true);
  };
  const persistConfidence = (next) => {
    player.set("quiz_pre_confidence", next);
    player.set("quiz_pre_partial", true);
  };

  const setRadio = (q, value) => {
    setAnswers((prev) => { const next = { ...prev, [q.id]: value }; persistAnswers(next); return next; });
    setShowError(false);
  };
  const toggleCheckbox = (q, value) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[q.id]) ? prev[q.id] : [];
      const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      const next = { ...prev };
      if (updated.length === 0) delete next[q.id]; else next[q.id] = updated;
      persistAnswers(next);
      return next;
    });
    setShowError(false);
  };
  const setText = (q, value) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (value && value.length > 0) next[q.id] = value; else delete next[q.id];
      persistAnswers(next);
      return next;
    });
    setShowError(false);
  };
  const setConf = (q, value) => {
    setConfidence((prev) => { const next = { ...prev, [q.id]: value }; persistConfidence(next); return next; });
    setShowError(false);
  };

  const total = questions.length;
  const completedCount = questions.filter(isAnswered).length;
  const allComplete = total > 0 && completedCount === total;
  const currentQuestion = questions[currentIndex];
  const isLast = currentIndex === total - 1;

  const handleFinishQuestions = () => {
    if (!allComplete) { setShowError(true); return; }
    setShowPrediction(true);
  };

  const baselineConfidenceComplete = isConfidenceComplete(baselineConfidence, []);

  const handleComplete = () => {
    if (prediction === null || prediction === undefined) { setShowError(true); return; }
    if (!baselineConfidenceComplete) { setShowError(true); return; }
    player.set("quiz_pre_answers", answers);
    player.set("quiz_pre_confidence", confidence);
    player.set("quiz_pre_prediction", prediction);
    // Baseline state confidence (same 2-item scale used post-learning/chat/quiz),
    // captured here after the pre-quiz and before any learning.
    player.set("baseline_state_confidence", {
      ...baselineConfidence,
      topicId: config?.id,
      timestamp: new Date().toISOString(),
    });
    // Baseline explanation-confidence step (dyad only, both roles) — its own
    // screen finishes the round. Single-mode learner has no partner → finish here.
    if (isTeacher || isStudent) { setShowError(false); setShowExplain(true); return; }
    player.set("quiz_pre_timestamp", new Date().toISOString());
    player.set("quiz_pre_partial", false);
    onComplete && onComplete();
  };

  // Teacher-only final step, shown after the baseline quiz is complete.
  const handleExplainComplete = () => {
    if (explainAbility === null) { setShowError(true); return; }
    player.set(explainField, {
      value: Number(explainAbility),
      topicId: config?.id,
      timestamp: new Date().toISOString(),
    });
    player.set("quiz_pre_timestamp", new Date().toISOString());
    player.set("quiz_pre_partial", false);
    onComplete && onComplete();
  };

  // Defensive: nothing to ask -> just continue
  if (total === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50">
        <button onClick={() => onComplete && onComplete()} className="py-3 px-6 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700">
          Continue
        </button>
      </div>
    );
  }

  // Teacher-only step shown AFTER the baseline quiz (prediction + confidence)
  // and before any learning — topic-specific explaining self-efficacy (baseline).
  if (showExplain) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-blue-600 rounded-xl p-5 mb-6 text-white">
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">Before you start learning</span>
            <h2 className="text-xl font-bold mt-1">One last question</h2>
            <p className="text-sm opacity-80 mt-1">{topicName}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isStudent
                ? "How well do you expect your partner will be able to explain this topic to you?"
                : "How well could you explain this topic to your partner right now?"}
            </label>
            <p className="text-xs text-gray-500 mb-3">(0 = Not at all well, 6 = Extremely well)</p>
            <div className="flex gap-1 justify-center">
              {SCALE_0_6.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setExplainAbility(String(v)); setShowError(false); }}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    explainAbility === String(v) ? "bg-blue-600 text-white shadow-md scale-110" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            {showError && explainAbility === null && (
              <p className="text-sm text-red-700 mt-3 text-center">Please choose a number to continue.</p>
            )}
            <div className="mt-6 flex items-center justify-between">
              <button type="button" onClick={() => setShowExplain(false)} className="py-2 px-4 rounded-lg font-medium text-gray-700 hover:bg-gray-100">
                Back
              </button>
              <button
                type="button"
                onClick={handleExplainComplete}
                className={`py-2 px-6 rounded-lg font-semibold text-white transition-all ${
                  explainAbility !== null ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Prediction screen (after all questions answered)
  if (showPrediction) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-blue-600 rounded-xl p-5 mb-6 text-white">
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">Pre-Learning Quiz</span>
            <h2 className="text-xl font-bold mt-1">One more thing</h2>
            <p className="text-sm opacity-80 mt-1">{topicName}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Of the {total} questions you just answered, how many do you think you got right?
            </label>
            <div className="flex gap-1 justify-center">
              {Array.from({ length: total + 1 }, (_, v) => v).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setPrediction(v); setShowError(false); }}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    prediction === v ? "bg-blue-600 text-white shadow-md scale-110" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            {showError && (prediction === null || prediction === undefined) && (
              <p className="text-sm text-red-700 mt-3 text-center">Please choose a number to continue.</p>
            )}

            {/* Baseline state confidence — same 2-item scale used at later timepoints */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-4">
                Before you start learning, how do you feel about this topic right now?
              </p>
              <ConfidenceScale
                values={baselineConfidence}
                onChange={(key, value) => { setBaselineConfidence((prev) => ({ ...prev, [key]: value })); setShowError(false); }}
                color="blue"
                topicName={topicName}
                startNumber={1}
                showHeader={false}
                includePrediction={false}
              />
            </div>

            {showError && !baselineConfidenceComplete && (
              <p className="text-sm text-red-700 mt-3 text-center">Please answer both confidence questions to continue.</p>
            )}

            <div className="mt-6 flex items-center justify-between">
              <button type="button" onClick={() => setShowPrediction(false)} className="py-2 px-4 rounded-lg font-medium text-gray-700 hover:bg-gray-100">
                Back
              </button>
              <button
                type="button"
                onClick={handleComplete}
                className={`py-2 px-6 rounded-lg font-semibold text-white transition-all ${
                  prediction !== null && prediction !== undefined && baselineConfidenceComplete ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Submit &amp; continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderOptions = (q) => {
    if (q.type === "text") {
      const value = answers[q.id] || "";
      return (
        <>
          <textarea
            value={value}
            onChange={(e) => setText(q, e.target.value)}
            maxLength={q.charLimit}
            rows={5}
            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
            placeholder="Type your response here..."
          />
          {q.charLimit ? (
            <p className="text-sm text-gray-500 mt-2 text-right">{value.length} / {q.charLimit} characters</p>
          ) : null}
        </>
      );
    }

    const isCheckbox = q.type === "checkbox";
    const current = answers[q.id];
    return (
      <div className="space-y-3">
        {(q.options || []).map((option, optIndex) => {
          const selected = isCheckbox
            ? Array.isArray(current) && current.includes(option.value)
            : current === option.value;
          const labelHasHtml = containsHtml(option.label);
          return (
            <label
              key={optIndex}
              className={`flex items-start cursor-pointer p-4 rounded-lg border-2 transition-all ${
                selected ? "bg-blue-50 border-blue-500" : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
              }`}
            >
              <input
                type={isCheckbox ? "checkbox" : "radio"}
                name={String(q.id)}
                value={option.value}
                checked={selected}
                onChange={() => (isCheckbox ? toggleCheckbox(q, option.value) : setRadio(q, option.value))}
                className={`mr-3 mt-0.5 h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 ${isCheckbox ? "rounded" : ""}`}
              />
              {labelHasHtml ? (
                <span className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: option.label }} />
              ) : (
                <span className="text-sm text-gray-700 leading-relaxed"><LatexMessage text={option.label} /></span>
              )}
            </label>
          );
        })}
      </div>
    );
  };

  // Per-question confidence (0-6), the only measure on the pre-quiz.
  const renderConfidence = (q) => {
    const v = confidence[q.id];
    return (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">How confident are you in your answer?</label>
        <div className="flex items-center gap-1 justify-center">
          <span className="text-xs text-gray-500 w-16 text-right">Not at all</span>
          <div className="flex gap-1">
            {SCALE_0_6.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setConf(q, n)}
                className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                  v === n ? "bg-blue-600 text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500 w-16">Very</span>
        </div>
      </div>
    );
  };

  const questionHasHtml = containsHtml(currentQuestion.text);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-blue-600 rounded-xl p-5 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium uppercase tracking-wide opacity-80">Pre-Learning Quiz</span>
              <h2 className="text-xl font-bold mt-1">Before you start: what do you already know?</h2>
              <p className="text-sm opacity-80 mt-1">{topicName}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{currentIndex + 1}<span className="text-lg opacity-70">/{total}</span></div>
              <div className="text-xs opacity-70">{completedCount} complete</div>
            </div>
          </div>

          {/* Progress Dots */}
          <div className="flex gap-1.5 mt-4 justify-center">
            {questions.map((q, idx) => {
              const done = isAnswered(q);
              const isCurrent = idx === currentIndex;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    isCurrent ? "bg-white scale-125" : done ? "bg-white/80" : "bg-white/30 hover:bg-white/50"
                  }`}
                  title={`Question ${idx + 1}${done ? " (answered)" : ""}`}
                />
              );
            })}
          </div>
        </div>

        {/* Instructions on first question */}
        {currentIndex === 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-gray-700">
              Answer these questions before you begin learning, and rate how confident you are in each answer. Don't worry if you're unsure — your best guess is fine. This just gives us a starting point.
            </p>
          </div>
        )}

        {/* Current question */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            {questionHasHtml ? (
              <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: currentQuestion.text }} />
            ) : (
              <h3 className="font-semibold text-gray-900 text-lg mb-4 leading-relaxed"><LatexMessage text={currentQuestion.text} /></h3>
            )}
            {currentQuestion.type === "checkbox" && <p className="text-sm text-gray-500 mb-4">Select all that apply</p>}
            {(!currentQuestion.type || currentQuestion.type === "radio") && <p className="text-sm text-gray-500 mb-4">Select one answer</p>}
            {renderOptions(currentQuestion)}
            {renderConfidence(currentQuestion)}
          </div>
        </div>

        {showError && !allComplete && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm font-medium text-red-700">
              Please answer all questions and rate your confidence before continuing. {total - completedCount} remaining.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
              className={`py-2 px-4 rounded-lg font-medium transition-all ${currentIndex === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
            >
              Previous
            </button>

            <div className="text-center text-sm">
              {isAnswered(currentQuestion) ? (
                <span className="text-green-600 font-medium">Answered</span>
              ) : (
                <span className="text-gray-500">Answer + confidence</span>
              )}
            </div>

            {isLast ? (
              <button
                type="button"
                onClick={handleFinishQuestions}
                className={`py-2 px-6 rounded-lg font-semibold text-white transition-all ${allComplete ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}`}
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCurrentIndex((i) => Math.min(total - 1, i + 1))}
                className={`py-2 px-4 rounded-lg font-medium transition-all ${isAnswered(currentQuestion) ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-gray-700 hover:bg-gray-100"}`}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
